import { Gray100, Modal, typeCompact14MediumProperties } from '@traceair/tds';
import { stylesheet } from 'typestyle';
import React, { useEffect } from 'react';
import { useAppStore } from '../../../../store/appStore';
import { GcpRequestData, useUploadStore } from '../../../../store/uploadStore';
import { ProjectData } from '../types';
import { useDiscardGcpChanges } from './gcpRequestUpload/gcpRequestSubmit/useDiscardGcpChanges';
import { useUiStore } from '../../../../store/uiStore';
import { MissionWindowTab } from '../../../../consts/missionWindowTab';

const css = stylesheet({
  modalText: {
    ...typeCompact14MediumProperties,
    color: Gray100,
  },
});

interface Props extends ProjectData {
  gcpRequest?: GcpRequestData;
  innerRef: React.RefObject<HTMLDivElement>;
}

export const GcpLeaveModal = ({ customer, site, gcpRequest, innerRef }: Props) => {
  const { pendingGcpLeaveAction, isGcpLeaveModalOpen, closeGcpLeaveModal, selectedMission } = useAppStore();
  const { hasGcpFormChanges } = useUploadStore();
  const { setSelectedMissionWindowTab } = useUiStore();
  const hasChanges = hasGcpFormChanges(customer.id, site.id);

  const handleDiscard = useDiscardGcpChanges(customer, site, gcpRequest);

  /**
   * Warn the user with a browser dialog if they attempt to leave with unsaved GCPs.
   */
  useEffect(() => {
    if (!gcpRequest || !hasChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue =
        'You’ve uploaded a GCP measurement file but haven’t sent it. If you leave now, the uploaded files will be deleted.';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gcpRequest, hasChanges]);

  /**
   * Executes the originally intended action (navigation/logout/etc) and clears pending changes.
   */
  const handleLeaveAnyway = () => {
    if (selectedMission) handleDiscard();
    pendingGcpLeaveAction?.();
    closeGcpLeaveModal();
  };

  /**
   * Keeps the user on the GCP page and scrolls to the GCP submission section.
   */
  const handleBackToGcps = () => {
    closeGcpLeaveModal();
    setSelectedMissionWindowTab(MissionWindowTab.Upload);
    setTimeout(() => {
      if (innerRef.current) {
        innerRef.current.scrollTo({
          top: innerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 0);
  };

  return (
    <Modal
      isOpen={isGcpLeaveModalOpen}
      size="m"
      closableByOverlayClick={false}
      onClose={handleBackToGcps}
      title="GCPs not sent"
      cancelButtonProps={{
        onClick: handleLeaveAnyway,
        text: 'Leave anyway',
      }}
      okButtonProps={{
        onClick: handleBackToGcps,
        text: 'Back to GCPs',
      }}
    >
      <div className={css.modalText}>
        You’ve uploaded a GCP measurement file but haven’t sent it. If you leave now, the uploaded files will be
        deleted.
      </div>
    </Modal>
  );
};
